import { useCallback, useMemo, useState } from 'react';

import { Modal, Text, dialogService } from '@thefarmersfront/kitchen';

import { InfoText, ModalContentsContainer, Table } from '~/components/common';

import { usePutReceivingLabelLabelsMutation } from '~/hooks/receive';
import { useUserInfoStore } from '~/store';
import { type TypeReceivingLabelRow, getReceiveLabelPrintModalColumnDefs } from '~/utils';
import { generateReceiveLabelPDF } from '~/utils/receive/pdf';

import { TOAST_MESSAGE } from '~/constants';

import type { TypeInputStatus } from '~/types/common.types';
import type { TypeReceivingLabelItem } from '~/types/receive.types';

import styles from './ReceiveLabelPrintModal.module.scss';

/**
 * 라벨 수 디폴트 값 계산
 * - EA만 있는 경우: 1
 * - BOX가 있는 경우: BOX 수량 + (EA가 있으면 +1)
 */
const calculateDefaultLabelCount = (box: number, unit: number): number => {
  if (box === 0) {
    return 1;
  }
  return box + (unit > 0 ? 1 : 0);
};

const ReceiveLabelPrintModal = ({
  open,
  data,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  data: TypeReceivingLabelItem[];
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const labelMutation = usePutReceivingLabelLabelsMutation();

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);

  const [tableData, setTableData] = useState<TypeReceivingLabelItem[]>([]);
  const [labelRequestsStatus, setLabelRequestsStatus] = useState<Record<number, TypeInputStatus>>({});
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

  const [prevData, setPrevData] = useState<TypeReceivingLabelItem[] | null>(null);
  if (data !== prevData) {
    setPrevData(data);
    if (data.length > 0) {
      const initialData = data.map((item) => ({
        ...item,
        label: calculateDefaultLabelCount(item.box, item.unit),
      }));
      setTableData(initialData);
      setLabelRequestsStatus({});
      // 모달이 열리면 전 상품을 선택한 채로 시작한다.
      setSelectedRowIds(initialData.map((item) => item.receivingRequestItemId));
    }
  }

  const handleLabelCountChange = useCallback((receivingRequestItemId: number, value: string) => {
    const numValue = value === '' ? 0 : Number(value);
    setTableData((prev) =>
      prev.map((item) => (item.receivingRequestItemId === receivingRequestItemId ? { ...item, label: numValue } : item))
    );
  }, []);

  const handleLabelCountFocus = useCallback((receivingRequestItemId: number) => {
    setLabelRequestsStatus((prev) => {
      const newStatus = { ...prev };
      delete newStatus[receivingRequestItemId];
      return newStatus;
    });
  }, []);

  const handleSelectionChange = (selectedRows: TypeReceivingLabelRow[]) => {
    setSelectedRowIds(selectedRows.map((row) => row.receivingRequestItemId));
  };

  const { columnDefs } = useMemo(
    () =>
      getReceiveLabelPrintModalColumnDefs({
        onLabelCountChange: handleLabelCountChange,
        onLabelCountFocus: handleLabelCountFocus,
      }),
    [handleLabelCountChange, handleLabelCountFocus]
  );
  const tableRows = useMemo<TypeReceivingLabelRow[]>(
    () => tableData.map((item) => ({ ...item, labelStatus: labelRequestsStatus[item.receivingRequestItemId] })),
    [tableData, labelRequestsStatus]
  );
  const selectedRowIdKeys = useMemo(() => selectedRowIds.map(String), [selectedRowIds]);

  const handleConfirm = () => {
    if (selectedRowIds.length === 0) {
      dialogService.addDialog({ children: '출력할 상품을 선택해주세요.' });
      return;
    }

    const selectedItems = tableData.filter((item) => selectedRowIds.includes(item.receivingRequestItemId));

    // 유효성 검증
    const errors: Record<number, TypeInputStatus> = {};
    let hasError = false;

    selectedItems.forEach((item) => {
      const { receivingRequestItemId, label, totalUnit } = item;

      if (label < 1 || label > totalUnit) {
        errors[receivingRequestItemId] = 'error';
        hasError = true;
      }
    });

    if (hasError) {
      setLabelRequestsStatus(errors);
      dialogService.addDialog({ children: '라벨 수는 1개 이상, 총 수량(EA) 이하로 입력해주세요.' });
      return;
    }

    const totalLabelCount = selectedItems.reduce((acc, item) => acc + item.label, 0);
    if (totalLabelCount > 200) {
      dialogService.addDialog({ children: '한 번에 최대 200개까지 출력 가능합니다.' });
      return;
    }

    // API 호출
    const requests = selectedItems.map((item) => ({
      receivingRequestItemId: item.receivingRequestItemId,
      labelCnt: item.label,
    }));

    labelMutation.mutate(
      { ownerCode, params: { requests } },
      {
        onSuccess: async (data) => {
          try {
            await generateReceiveLabelPDF(data.labels);
            onConfirm();
          } catch (error) {
            dialogService.addDialog({
              children: error instanceof Error ? error.message : 'PDF 생성에 실패했습니다.',
            });
          }
        },
        onError: (error) => {
          dialogService.addDialog({ children: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  return (
    <Modal
      title={
        <div className={styles['modal-title']}>
          <Text size="xxlarge" weight="semi-bold">
            입하 라벨 출력
          </Text>
          <div>
            <InfoText size="large" bulletColor="#565E67">
              EA 단위는 라벨 수가 1로 기본 설정되어 있어요.
            </InfoText>
            <InfoText size="large" bulletColor="#565E67">
              출력하시기 전에 필요한 만큼 개수를 변경하고 인쇄해 주세요.
            </InfoText>
          </div>
        </div>
      }
      size="xlarge"
      confirmText="출력"
      disabled={selectedRowIds.length === 0}
      open={open}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    >
      <ModalContentsContainer className={styles['modal-content']}>
        <Table
          className={styles['table']}
          data={tableRows}
          columns={columnDefs}
          getRowId={(row) => String(row.receivingRequestItemId)}
          keepScrollOnDataChange
          selection={{ selectedRowIds: selectedRowIdKeys, onChange: handleSelectionChange }}
        />
      </ModalContentsContainer>
    </Modal>
  );
};

export default ReceiveLabelPrintModal;
